import React, { useState } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { BaseModal } from '@/components/shared';
import { ColumnConfig, PresetConfig } from '../hooks/useColumnVisibility';

interface ColumnVisibilityModalProps {
    isOpen: boolean;
    onClose: () => void;
    availableColumns: ColumnConfig[];
    presets: PresetConfig[];
    columnVisibility: Record<string, boolean>;
    onToggleColumn: (columnId: string) => void;
    onApplyPreset: (presetId: string) => void;
    onToggleAllColumns: (visible: boolean) => void;
    onResetToDefault: () => void;
}

type TabType = 'presets' | 'categories' | 'all';

const ColumnVisibilityModal: React.FC<ColumnVisibilityModalProps> = ({
    isOpen,
    onClose,
    availableColumns,
    presets,
    columnVisibility,
    onToggleColumn,
    onApplyPreset,
    onToggleAllColumns,
    onResetToDefault,
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('presets');

    // Agrupar colunas por categoria
    const columnsByCategory = availableColumns.reduce((acc, column) => {
        if (!acc[column.category]) {
            acc[column.category] = [];
        }
        acc[column.category].push(column);
        return acc;
    }, {} as Record<string, ColumnConfig[]>);

    const categories = Object.keys(columnsByCategory);

    // Estatísticas das colunas
    const visibleColumns = availableColumns.filter(col => columnVisibility[col.id] !== false);
    const hiddenColumns = availableColumns.filter(col => columnVisibility[col.id] === false);
    const hideableColumns = availableColumns.filter(col => col.canHide);

    const actions = (
        <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-600">
                {visibleColumns.length} de {availableColumns.length} colunas visíveis
                {hiddenColumns.length > 0 && (
                    <span className="ml-2">• {hiddenColumns.length} ocultas</span>
                )}
            </div>
            <div className="flex gap-3">
                <button
                    onClick={onResetToDefault}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                    Resetar Padrão
                </button>
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                >
                    Concluído
                </button>
            </div>
        </div>
    );

    return (
        <BaseModal 
            isOpen={isOpen} 
            onClose={onClose} 
            title="Configurar Colunas"
            maxWidth="max-w-4xl"
            actions={actions}
        >
            {/* Tabs */}
            <div className="border-b border-gray-200 -mx-6 mb-6">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                    {[
                        { id: 'presets', label: 'Presets', count: presets.length },
                        { id: 'categories', label: 'Por Categoria', count: categories.length },
                        { id: 'all', label: 'Todas', count: hideableColumns.length },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as TabType)}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === tab.id
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {tab.label}
                            <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                                {tab.count}
                            </span>
                        </button>
                    ))}
                </nav>
                </div>

            {/* Content */}
            <div className="flex-1">
                {activeTab === 'presets' && (
                    <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {presets.map((preset) => (
                                    <div
                                        key={preset.id}
                                        className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors cursor-pointer"
                                        onClick={() => {
                                            onApplyPreset(preset.id);
                                            onClose();
                                        }}
                                    >
                                        <h3 className="font-medium text-gray-900 mb-1">{preset.label}</h3>
                                        <p className="text-sm text-gray-600 mb-3">{preset.description}</p>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500">
                                                {preset.columns.length} colunas
                                            </span>
                                            <button 
                                                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Evita que o click do div pai seja executado também
                                                    onApplyPreset(preset.id);
                                                    onClose();
                                                }}
                                            >
                                                Aplicar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                    </div>
                )}

                {activeTab === 'categories' && (
                    <div className="space-y-6">
                            {categories.map((category) => {
                                const categoryColumns = columnsByCategory[category];
                                const visibleInCategory = categoryColumns.filter(col => columnVisibility[col.id] !== false).length;
                                const hideableInCategory = categoryColumns.filter(col => col.canHide);

                                return (
                                    <div key={category} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-medium text-gray-900">
                                                {category}
                                                <span className="ml-2 text-sm text-gray-500">
                                                    ({visibleInCategory}/{categoryColumns.length})
                                                </span>
                                            </h3>
                                            {hideableInCategory.length > 0 && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            hideableInCategory.forEach(col => {
                                                                if (!columnVisibility[col.id]) {
                                                                    onToggleColumn(col.id);
                                                                }
                                                            });
                                                        }}
                                                        className="text-xs text-gray-600 hover:text-gray-800"
                                                    >
                                                        Mostrar Todas
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            hideableInCategory.forEach(col => {
                                                                if (columnVisibility[col.id] !== false) {
                                                                    onToggleColumn(col.id);
                                                                }
                                                            });
                                                        }}
                                                        className="text-xs text-gray-600 hover:text-gray-800"
                                                    >
                                                        Ocultar Todas
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {categoryColumns.map((column) => (
                                                <label
                                                    key={column.id}
                                                    className={`flex items-center space-x-3 p-2 rounded-md transition-colors ${
                                                        column.canHide
                                                            ? 'hover:bg-gray-50 cursor-pointer'
                                                            : 'opacity-50 cursor-not-allowed'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={columnVisibility[column.id] !== false}
                                                        onChange={() => column.canHide && onToggleColumn(column.id)}
                                                        disabled={!column.canHide}
                                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                    />
                                                    <span className="text-sm text-gray-700 flex-1">{column.label}</span>
                                                    {!column.canHide && (
                                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                            Essencial
                                                        </span>
                                                    )}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                )}

                {activeTab === 'all' && (
                    <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="font-medium text-gray-900">Todas as Colunas</h3>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => onToggleAllColumns(true)}
                                        className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                        <EyeIcon className="h-4 w-4" />
                                        Mostrar Todas
                                    </button>
                                    <button
                                        onClick={() => onToggleAllColumns(false)}
                                        className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                                    >
                                        <EyeSlashIcon className="h-4 w-4" />
                                        Ocultar Todas
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {availableColumns.map((column) => (
                                    <label
                                        key={column.id}
                                        className={`flex items-center space-x-3 p-3 border border-gray-200 rounded-md transition-colors ${
                                            column.canHide
                                                ? 'hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer'
                                                : 'opacity-50 cursor-not-allowed bg-gray-50'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={columnVisibility[column.id] !== false}
                                            onChange={() => column.canHide && onToggleColumn(column.id)}
                                            disabled={!column.canHide}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium text-gray-700 block">{column.label}</span>
                                            <span className="text-xs text-gray-500">{column.category}</span>
                                        </div>
                                        {!column.canHide && (
                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                Essencial
                                            </span>
                                        )}
                                    </label>
                                ))}
                            </div>
                    </div>
                )}
            </div>

        </BaseModal>
    );
};

export default ColumnVisibilityModal;